import { useEffect, useState } from "react";
import { Building, Lock, MapPin, Tag } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "@/lib/toast";
import api from "@/lib/api";
import { useRequiredFields } from "@/lib/useRequiredFields";
import { FieldError } from "@/components/common/RequiredField";

const REF_RE = /^[A-Z0-9-]{2,10}$/;
const normRef = (v) => (v || "").toUpperCase().replace(/\s+/g, "").replace(/[^A-Z0-9-]/g, "").slice(0, 10);
const V = "#5B5FC7", FOCUS = "#7a6cf0";

/** Fiche projet (création / modification) — iter356 : UNIQUEMENT Référence, Nom, Adresse.
 * Formule → onglet Paramètres du projet · TVA → taux du lot · Livraison → dernière étape des travaux. */
export default function ProjectDialog({ open, onClose, project = null, onSaved }) {
  const [form, setForm] = useState({ reference: "", name: "", address: "" });
  const [busy, setBusy] = useState(false);
  const [focus, setFocus] = useState(null);
  useEffect(() => { if (open) setForm({ reference: project?.reference || "", name: project?.project_name ?? project?.name ?? "", address: project?.address || project?.location || "" }); }, [open, project]);
  const set = (k) => (e) => setForm({ ...form, [k]: k === "reference" ? normRef(e.target.value) : e.target.value });
  const refLocked = !!project?.reference_locked;
  const refOk = REF_RE.test(form.reference);
  const req = useRequiredFields([
    { key: "reference", label: "Référence projet", ok: refOk },
    { key: "name", label: "Nom du projet", ok: !!form.name.trim() },
  ]);
  const save = async () => {
    if (!req.validate()) return;
    setBusy(true);
    try {
      const body = { name: form.name, address: form.address, ...(refLocked ? {} : { reference: form.reference }) };
      const { data } = project ? await api.patch(`/crm/projects/${project.id}`, body) : await api.post("/crm/projects", body);
      toast.success(project ? "Projet modifié" : "Projet créé");
      onSaved?.(data); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };
  const field = (k, { label, Icon, iconColor, placeholder, required, bold, disabled, width, autoFocus, maxLength }) => {
    const err = req.has(k);
    return (
      <div data-field={k} style={{ width: width || undefined, flex: width ? "none" : 1, minWidth: 0 }}>
        <div className="flex items-center" style={{ gap: 5, fontSize: 8.5, textTransform: "uppercase", letterSpacing: ".4px", color: "#8b8b93", marginBottom: 4 }}>{disabled ? <Lock size={10} style={{ color: "#E0A42E" }} /> : <Icon size={11} style={{ color: iconColor }} />}{label}{required && <span style={{ color: "#E0A42E" }}>*</span>}</div>
        <input value={form[k]} onChange={set(k)} onFocus={() => setFocus(k)} onBlur={() => setFocus(null)} placeholder={placeholder} disabled={disabled} autoFocus={autoFocus} maxLength={maxLength}
          className={bold ? "tabular-nums tracking-wide" : ""}
          style={{ width: "100%", background: "#18181d", border: `1px solid ${err ? "#c0392b" : focus === k ? FOCUS : "#2a2a30"}`, boxShadow: focus === k ? `0 0 0 3px rgba(122,108,240,.18)` : "none", borderRadius: 8, padding: "8px 10px", fontSize: 13, color: bold ? "#c9c6f7" : "#ececf0", fontWeight: bold ? 700 : 400, outline: "none", opacity: disabled ? .7 : 1 }}
          data-testid={`crm-project-${k}`} />
        <FieldError show={err} testId={`crm-project-${k}-error`} />
      </div>
    );
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" style={{ background: "#111114", border: "0.5px solid #26262a" }} data-testid="crm-project-dialog">
        <DialogHeader><DialogTitle style={{ fontSize: 14 }}>{project ? "Modifier le projet" : "Nouveau projet"}</DialogTitle></DialogHeader>
        <section className="flex" style={{ background: "#141418", border: "0.5px solid #26262a", borderRadius: 10, overflow: "hidden" }} data-testid="crm-project-identity-block">
          <div className="flex items-start justify-center shrink-0" style={{ width: 30, background: V, paddingTop: 12 }}><Building size={15} color="#fff" /></div>
          <div style={{ flex: 1, padding: "11px 14px", minWidth: 0 }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "#a9abf2", marginBottom: 10 }}>Identité du projet</div>
            <div className="flex" style={{ gap: 10 }}>
              {field("reference", { label: "Réf.", Icon: Tag, iconColor: "#b3a6ff", placeholder: "Ex. : C01", required: true, bold: true, disabled: refLocked, width: 100, autoFocus: !project, maxLength: 10 })}
              {field("name", { label: "Nom", Icon: Building, iconColor: "#b3a6ff", placeholder: "Ex. : Résidence Les Jardins", required: true })}
            </div>
            <div className="flex" style={{ gap: 10, marginTop: 10 }}>
              {field("address", { label: "Adresse", Icon: MapPin, iconColor: "#ed93b1", placeholder: "Ex. : Rue, n°, commune" })}
            </div>
            <div style={{ fontSize: 9, color: refLocked ? "#E0A42E" : "#71717c", marginTop: 8 }} data-testid="crm-project-reference-hint">
              {refLocked ? "Référence verrouillée : des devis, réservations ou ventes portent déjà cette référence dans leur numéro." : <>Réf. : 2 à 10 caractères · n° de dossier <b style={{ color: "#c9c6f7" }}>V-{form.reference || "C01"}-0001</b></>}
            </div>
          </div>
        </section>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="crm-project-cancel">Annuler</Button>
          <Button onClick={save} disabled={busy} style={{ background: FOCUS, color: "#fff" }} data-testid="crm-project-save">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
